import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { getDashboardStats, getLeads } from '@/lib/api';
import { LogOut, Users, FileText, Mail, Search, Eye, Trash2, Send, Bot, BarChart3, Calendar, Euro, Sparkles, Palette, Gift } from 'lucide-react';
import { User, Session } from '@supabase/supabase-js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BlogManagement } from '@/components/dashboard/BlogManagement';
import { BotManagement } from '@/components/dashboard/BotManagement';
import ChatAnalytics from '@/components/dashboard/ChatAnalytics';
import { AppointmentManagement } from '@/components/dashboard/AppointmentManagement';
import PriceOverview from '@/components/dashboard/PriceOverview';
import { KompaktAdmin } from '@/components/dashboard/KompaktAdmin';
import { ThemesManagement } from '@/components/dashboard/ThemesManagement';
import { VoucherManagement } from '@/components/dashboard/VoucherManagement';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface Lead {
  id: string;
  name: string;
  email: string;
  phone?: string;
  message?: string;
  service?: string;
  newsletter_opt_in?: boolean;
  created_at: string;
}

interface Stats {
  total_leads: number;
  total_articles: number;
}

interface ReplyForm {
  subject: string;
  message: string;
}

function Dashboard() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState<Stats>({ total_leads: 0, total_articles: 0 });
  const [leads, setLeads] = useState<Lead[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [replyForm, setReplyForm] = useState<ReplyForm>({ subject: '', message: '' });
  const [sendingReply, setSendingReply] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [activeTab, setActiveTab] = useState('leads');
  const navigate = useNavigate();
  const { toast } = useToast();

  const checkAdminRole = async (userId: string): Promise<boolean> => {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .eq('role', 'admin')
      .single();
    
    return !error && !!data;
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (!session?.user) {
          navigate('/auth');
        } else {
          // Defer admin check to avoid Supabase deadlock
          setTimeout(() => {
            checkAdminRole(session.user.id).then(hasAdminRole => {
              if (!hasAdminRole) {
                toast({
                  title: 'Zugriff verweigert',
                  description: 'Sie haben keine Admin-Berechtigung für diesen Bereich.',
                  variant: 'destructive',
                });
                navigate('/');
              } else {
                setIsAdmin(true);
              }
            });
          }, 0);
        }
      }
    );

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (!session?.user) {
        navigate('/auth');
        setLoading(false);
      } else {
        const hasAdminRole = await checkAdminRole(session.user.id);
        if (!hasAdminRole) {
          toast({
            title: 'Zugriff verweigert',
            description: 'Sie haben keine Admin-Berechtigung für diesen Bereich.',
            variant: 'destructive',
          });
          navigate('/');
          setLoading(false);
          return;
        }
        setIsAdmin(true);
        loadData();
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const loadData = async () => {
    try {
      const [statsData, leadsData] = await Promise.all([
        getDashboardStats(),
        getLeads(),
      ]);
      setStats(statsData);
      setLeads(leadsData || []);
    } catch (error: any) {
      console.error('Error loading dashboard data:', error);
      toast({
        title: 'Fehler beim Laden',
        description: 'Daten konnten nicht geladen werden. Möglicherweise fehlen Admin-Rechte.',
        variant: 'destructive',
      });
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    toast({
      title: 'Abgemeldet',
      description: 'Sie wurden erfolgreich abgemeldet.',
    });
    navigate('/auth');
  };

  const deleteLead = async (id: string) => {
    try {
      const { error } = await supabase.from('leads').delete().eq('id', id);
      if (error) throw error;
      setLeads(leads.filter(l => l.id !== id));
      toast({
        title: 'Lead gelöscht',
        description: 'Der Lead wurde erfolgreich entfernt.',
      });
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const deleteAllLeads = async () => {
    try {
      const { error } = await supabase.from('leads').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      if (error) throw error;
      setLeads([]);
      toast({
        title: 'Alle Leads gelöscht',
        description: 'Alle Leads wurden erfolgreich entfernt.',
      });
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const toggleNewsletterStatus = async (id: string, status: boolean) => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({ newsletter_opt_in: status })
        .eq('id', id);
      if (error) throw error;
      setLeads(leads.map(l => l.id === id ? { ...l, newsletter_opt_in: status } : l));
      toast({
        title: 'Newsletter-Status aktualisiert',
        description: status ? 'Newsletter aktiviert' : 'Newsletter deaktiviert',
      });
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const sendReply = async (leadId: string) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;

    setSendingReply(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-reply-email', {
        body: {
          to: lead.email,
          name: lead.name,
          subject: replyForm.subject,
          message: replyForm.message,
        },
      });

      if (error) throw error;

      toast({
        title: 'E-Mail gesendet',
        description: `Antwort wurde an ${lead.email} gesendet.`,
      });
      setReplyForm({ subject: '', message: '' });
      setSelectedLead(null);
    } catch (error: any) {
      toast({
        title: 'Fehler beim Senden',
        description: error.message || 'E-Mail konnte nicht gesendet werden. Bitte RESEND_API_KEY konfigurieren.',
        variant: 'destructive',
      });
    } finally {
      setSendingReply(false);
    }
  };

  const filteredLeads = leads.filter(lead =>
    lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (lead.message && lead.message.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // Determine target group based on service field
  const getTargetGroup = (lead: Lead) => {
    if (lead.service?.toLowerCase().includes('business') || lead.service?.toLowerCase().includes('firma')) {
      return { label: 'Business', icon: '💼', bgClass: 'bg-blue-100 text-blue-800' };
    }
    return { label: 'Privat/Senior', icon: '👴', bgClass: 'bg-green-100 text-green-800' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex flex-col">
            <h1 className="text-2xl font-bold text-slate-900">Klartech Müritz</h1>
            <span className="text-sm text-slate-500">IT-Support & Verwaltung</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Abmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-cyan-500 to-teal-600 text-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium opacity-90 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Leads / Anfragen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.total_leads}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium opacity-90 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Blog-Artikel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.total_articles}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium opacity-90 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Newsletter Opt-ins
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {leads.filter(l => l.newsletter_opt_in).length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for different sections */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Mobile: Dropdown Select */}
          <div className="md:hidden">
            <Select value={activeTab} onValueChange={setActiveTab}>
              <SelectTrigger className="w-full bg-white border">
                <SelectValue placeholder="Bereich wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="leads">
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Kontaktanfragen
                  </span>
                </SelectItem>
                <SelectItem value="blog">
                  <span className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Blog-Verwaltung
                  </span>
                </SelectItem>
                <SelectItem value="bot">
                  <span className="flex items-center gap-2">
                    <Bot className="w-4 h-4" />
                    Bot-Wissen
                  </span>
                </SelectItem>
                <SelectItem value="analytics">
                  <span className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Chat-Statistik
                  </span>
                </SelectItem>
                <SelectItem value="appointments">
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Termine
                  </span>
                </SelectItem>
                <SelectItem value="prices">
                  <span className="flex items-center gap-2">
                    <Euro className="w-4 h-4" />
                    Preise
                  </span>
                </SelectItem>
                <SelectItem value="kompakt">
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Kompakt
                  </span>
                </SelectItem>
                <SelectItem value="themes">
                  <span className="flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Themes
                  </span>
                </SelectItem>
                <SelectItem value="vouchers">
                  <span className="flex items-center gap-2">
                    <Gift className="w-4 h-4" />
                    Gutscheine
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Desktop: Regular Tabs */}
          <TabsList className="hidden md:flex bg-white border">
            <TabsTrigger value="leads" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-cyan-700">
              <Users className="w-4 h-4 mr-2" />
              Kontaktanfragen
            </TabsTrigger>
            <TabsTrigger value="blog" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-cyan-700">
              <FileText className="w-4 h-4 mr-2" />
              Blog-Verwaltung
            </TabsTrigger>
            <TabsTrigger value="bot" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-cyan-700">
              <Bot className="w-4 h-4 mr-2" />
              Bot-Wissen
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-cyan-700">
              <BarChart3 className="w-4 h-4 mr-2" />
              Chat-Statistik
            </TabsTrigger>
            <TabsTrigger value="appointments" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-cyan-700">
              <Calendar className="w-4 h-4 mr-2" />
              Termine
            </TabsTrigger>
            <TabsTrigger value="prices" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-cyan-700">
              <Euro className="w-4 h-4 mr-2" />
              Preise
            </TabsTrigger>
            <TabsTrigger value="kompakt" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-cyan-700">
              <Sparkles className="w-4 h-4 mr-2" />
              Kompakt
            </TabsTrigger>
            <TabsTrigger value="themes" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-cyan-700">
              <Palette className="w-4 h-4 mr-2" />
              Themes
            </TabsTrigger>
            <TabsTrigger value="vouchers" className="data-[state=active]:bg-cyan-50 data-[state=active]:text-cyan-700">
              <Gift className="w-4 h-4 mr-2" />
              Gutscheine
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads">
            <Card>
              <CardHeader className="border-b bg-slate-50">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Lead Management
                  </CardTitle>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Alle löschen
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Alle Leads löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Diese Aktion kann nicht rückgängig gemacht werden. Alle {leads.length} Leads werden dauerhaft gelöscht.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction onClick={deleteAllLeads} className="bg-red-600 hover:bg-red-700">
                          Alle löschen
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                <div className="relative max-w-md mt-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Leads durchsuchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {filteredLeads.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">Keine Anfragen gefunden</p>
                    <p className="text-sm">Neue Kontaktanfragen erscheinen hier.</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop Table View */}
                    <div className="hidden lg:block overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="font-semibold">Name</TableHead>
                            <TableHead className="font-semibold">E-Mail</TableHead>
                            <TableHead className="font-semibold">Telefon</TableHead>
                            <TableHead className="font-semibold">Firma / Zielgruppe</TableHead>
                            <TableHead className="font-semibold">Newsletter</TableHead>
                            <TableHead className="font-semibold">Datum</TableHead>
                            <TableHead className="font-semibold">Aktionen</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredLeads.map((lead) => {
                            const targetGroup = getTargetGroup(lead);
                            return (
                              <TableRow key={lead.id} className="hover:bg-slate-50">
                                <TableCell className="font-medium text-cyan-700">{lead.name}</TableCell>
                                <TableCell>{lead.email}</TableCell>
                                <TableCell>{lead.phone || '-'}</TableCell>
                                <TableCell>
                                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${targetGroup.bgClass}`}>
                                    {targetGroup.icon} {targetGroup.label}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <span className={`px-2 py-1 text-xs rounded-full ${lead.newsletter_opt_in ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                      {lead.newsletter_opt_in ? '✅ Aktiv' : '❌ Nicht aktiv'}
                                    </span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={() => toggleNewsletterStatus(lead.id, !lead.newsletter_opt_in)}
                                      title={lead.newsletter_opt_in ? 'Newsletter deaktivieren' : 'Newsletter aktivieren'}
                                    >
                                      {lead.newsletter_opt_in ? '🔕' : '📧'}
                                    </Button>
                                  </div>
                                </TableCell>
                                <TableCell>{formatDate(lead.created_at)}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <Dialog>
                                      <DialogTrigger asChild>
                                        <Button 
                                          variant="outline" 
                                          size="sm"
                                          onClick={() => setSelectedLead(lead)}
                                        >
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                      </DialogTrigger>
                                      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                                        <DialogHeader>
                                          <DialogTitle>Lead Details: {lead.name}</DialogTitle>
                                        </DialogHeader>
                                        <LeadDetailContent 
                                          lead={lead}
                                          replyForm={replyForm}
                                          setReplyForm={setReplyForm}
                                          sendingReply={sendingReply}
                                          sendReply={sendReply}
                                          formatDateTime={formatDateTime}
                                          getTargetGroup={getTargetGroup}
                                        />
                                      </DialogContent>
                                    </Dialog>
                                    <Button 
                                      variant="destructive" 
                                      size="sm"
                                      onClick={() => deleteLead(lead.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="lg:hidden p-4 space-y-4">
                      {filteredLeads.map((lead) => {
                        const targetGroup = getTargetGroup(lead);
                        return (
                          <div key={lead.id} className="bg-white border rounded-lg p-4 shadow-sm">
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <h3 className="font-semibold text-cyan-700">{lead.name}</h3>
                                <p className="text-sm text-slate-600">{lead.email}</p>
                                {lead.phone && <p className="text-sm text-slate-500">{lead.phone}</p>}
                              </div>
                              <div className="flex items-center gap-1">
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button variant="outline" size="sm" onClick={() => setSelectedLead(lead)}>
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto mx-4">
                                    <DialogHeader>
                                      <DialogTitle>Lead: {lead.name}</DialogTitle>
                                    </DialogHeader>
                                    <LeadDetailContent 
                                      lead={lead}
                                      replyForm={replyForm}
                                      setReplyForm={setReplyForm}
                                      sendingReply={sendingReply}
                                      sendReply={sendReply}
                                      formatDateTime={formatDateTime}
                                      getTargetGroup={getTargetGroup}
                                    />
                                  </DialogContent>
                                </Dialog>
                                <Button 
                                  variant="destructive" 
                                  size="sm"
                                  onClick={() => deleteLead(lead.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${targetGroup.bgClass}`}>
                                {targetGroup.icon} {targetGroup.label}
                              </span>
                              <span className={`px-2 py-1 text-xs rounded-full ${lead.newsletter_opt_in ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                {lead.newsletter_opt_in ? '✅ Newsletter' : '❌ Kein Newsletter'}
                              </span>
                              <span className="text-xs text-slate-400">{formatDate(lead.created_at)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="blog">
            <BlogManagement />
          </TabsContent>

          <TabsContent value="bot">
            <BotManagement />
          </TabsContent>

          <TabsContent value="analytics">
            <ChatAnalytics />
          </TabsContent>

          <TabsContent value="appointments">
            <AppointmentManagement />
          </TabsContent>

          <TabsContent value="prices">
            <PriceOverview />
          </TabsContent>

          <TabsContent value="kompakt">
            <KompaktAdmin />
          </TabsContent>

          <TabsContent value="themes">
            <ThemesManagement />
          </TabsContent>

          <TabsContent value="vouchers">
            <VoucherManagement />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// Separate component for lead detail content
interface LeadDetailContentProps {
  lead: Lead;
  replyForm: ReplyForm;
  setReplyForm: React.Dispatch<React.SetStateAction<ReplyForm>>;
  sendingReply: boolean;
  sendReply: (leadId: string) => void;
  formatDateTime: (dateString: string) => string;
  getTargetGroup: (lead: Lead) => { label: string; icon: string; bgClass: string };
}

function LeadDetailContent({ 
  lead, 
  replyForm, 
  setReplyForm, 
  sendingReply, 
  sendReply, 
  formatDateTime,
  getTargetGroup 
}: LeadDetailContentProps) {
  const targetGroup = getTargetGroup(lead);
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-slate-600">Name:</label>
          <p className="text-sm mt-1">{lead.name}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-600">E-Mail:</label>
          <p className="text-sm mt-1 break-all">{lead.email}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-600">Telefon:</label>
          <p className="text-sm mt-1">{lead.phone || 'Nicht angegeben'}</p>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-600">Firma / Zielgruppe:</label>
          <p className="text-sm mt-1">
            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${targetGroup.bgClass}`}>
              {targetGroup.icon} {targetGroup.label}
            </span>
          </p>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-600">Newsletter:</label>
          <p className="text-sm mt-1">
            <span className={`px-2 py-1 text-xs rounded-full ${lead.newsletter_opt_in ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
              {lead.newsletter_opt_in ? '✅ Aktiv' : '❌ Nicht aktiv'}
            </span>
          </p>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-600">Datum:</label>
          <p className="text-sm mt-1">{formatDateTime(lead.created_at)}</p>
        </div>
      </div>
      
      <div>
        <label className="text-sm font-medium text-slate-600">Nachricht:</label>
        <p className="text-sm mt-2 p-3 bg-slate-50 rounded-lg break-words">
          {lead.message || 'Keine Nachricht'}
        </p>
      </div>
      
      <div className="border-t pt-4">
        <h4 className="font-medium mb-3">Antwort senden:</h4>
        <div className="space-y-4">
          <Input
            placeholder="Betreff"
            value={replyForm.subject}
            onChange={(e) => setReplyForm(prev => ({ ...prev, subject: e.target.value }))}
          />
          <Textarea
            placeholder="Ihre Antwort..."
            rows={4}
            value={replyForm.message}
            onChange={(e) => setReplyForm(prev => ({ ...prev, message: e.target.value }))}
          />
          <Button
            onClick={() => sendReply(lead.id)}
            disabled={sendingReply || !replyForm.subject || !replyForm.message}
            className="w-full bg-cyan-600 hover:bg-cyan-700"
          >
            {sendingReply ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Senden...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                E-Mail senden
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;